import MuiAlert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import Select from "@mui/material/Select";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import { TreeSelect } from "primereact/treeselect";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { globalConfig } from "../configuration/config";
import noPhoto from "../images/no-photo.jpg";
import { setOpenAnalytics } from "../store/reducers/analyticsSlice";
import { setOpen } from "../store/reducers/changeModelSlice";
import { setList } from "../store/reducers/linkSlice";
import { closePublicLinkForm } from "../store/reducers/publicLinkSlice";
import ChangeModel from "./forms/ChangeModel";
import Network from "./Requests";
import { useTranslation } from "react-i18next";

const PublicLinkNew = () => {
  const { open, defaultValues } = useSelector((state) => state.publicLink);
  const currentUser = useSelector((state) => state.users.currentUser);
  const companyId = useSelector((state) => state.project.companyId);
  const calculationList = useSelector((state) => state.variant?.list);
  const guidCalculation = useSelector(
    (state) => state.variant?.current?.calculationGuid
  );
  const groups = useSelector((state) => state.guids?.groups) || [];

  const { t } = useTranslation();
  const channels = t("linksPage.channels", { returnObjects: true });
  const types = t("linksPage.types", { returnObjects: true });

  const [usersList, setUsersList] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [publicLink, setPublicLink] = useState("");
  const [showQR, setShowQR] = useState(false);

  const dispatch = useDispatch();

  const [formData, setFormData] = useState(() => {
    // Вычисляем дефолтные папки один раз
    let defaultClientGroup = "";
    let defaultEmployeeGroup = "";
    let defaultOrderGroup = "";

    if (!defaultValues.guidDestinationGroup && groups.length) {
      const found = groups.find((g) => g.name === t("publicLinkForm.defaults.visits"));
      if (found) defaultClientGroup = found.guid;
    }

    if (!defaultValues.guidDestinationGroupForEmployee && groups.length) {
      const found = groups.find((g) => g.name === t("publicLinkForm.defaults.shows"));
      if (found) defaultEmployeeGroup = found.guid;
    }

    if (!defaultValues.idStartCatalogFolder && groups.length) {
      const found = groups.find(
        (g) =>
          g.name === t("publicLinkForm.defaults.firstContactsShort") ||
          g.name === t("publicLinkForm.defaults.firstContacts")
      );
      if (found) defaultOrderGroup = found.guid;
    }

    // ✅ Формируем локально полную ссылку, НЕ мутируем defaultValues
    let publicLink = defaultValues.publicLinkUrl;
    if (publicLink && !publicLink.startsWith("http")) {
      publicLink = `${globalConfig.config.common.chat}/web/public/?id=${publicLink}`;
    }
    return {
      activeFrom: "",
      activeTo: "",
      salesChanel: 1,
      guidUser: currentUser?.guid || "",
      type: 4,
      guidDestinationGroup:
        defaultValues.guidDestinationGroup ?? defaultClientGroup,
      guidDestinationGroupForEmployee:
        defaultValues.guidDestinationGroupForEmployee ?? defaultEmployeeGroup,
      externalUrl: "",
      showSaveVariantButton: true,
      showPrice: false,
      startup3DType: 2,
      varietyType: 2,
      companyPhoneNumber: defaultValues.companyPhoneNumber ?? "4992167212",
      feedbackFormUrl:
        defaultValues.feedbackFormUrl ?? "https://interior-techno.ru/#order",
      calculationName: defaultValues.calculationName ?? "",
      idStartCatalogFolder: "",
      ...defaultValues,
      publicLinkUrl: publicLink,
    };
  });

  const formatDateWithLocalOffset = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const offsetMinutes = -d.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absOffset = Math.abs(offsetMinutes);
    const hours = String(Math.floor(absOffset / 60)).padStart(2, "0");
    const minutes = String(absOffset % 60).padStart(2, "0");
    const iso = d.toISOString().slice(0, 19);
    return `${iso}${sign}${hours}:${minutes}`;
  };

  // Загрузка пользователей
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const a = await new Network().GetActiveCompanyUsers(companyId);
        const userExists = a.some((user) => user.guid === currentUser?.guid);
        const updatedUsers = userExists ? a : [...a, currentUser];
        setUsersList(updatedUsers);
      } catch (error) {
        console.error("Ошибка загрузки пользователей", error);
      }
    };
    if (companyId) fetchUsers();
  }, [companyId, currentUser]);

  useEffect(() => {
    if (defaultValues) {
      const findGroupByName = (names) => {
        if (!groups.length) return "";
        const found = groups.find((g) => names.includes(g.name));
        return found ? found.guid : "";
      };

      let publicLink = defaultValues.publicLinkUrl;
      if (publicLink && !publicLink.startsWith("http")) {
        publicLink = `${globalConfig.config.common.chat}/web/public/?id=${publicLink}`;
      }

      setFormData((prev) => ({
        ...prev,
        ...defaultValues,
        calculationName:
          defaultValues.calculationName ?? prev.calculationName ?? "",
        iconPath: defaultValues.iconPath ?? prev.iconPath ?? "",
        guidProject: defaultValues.guidProject ?? prev.guidProject ?? "",

        // 🔥 Подхватываем guidCalculation из редакса, если нет в defaultValues
        guidCalculation:
          defaultValues.guidCalculation ??
          prev.guidCalculation ??
          guidCalculation ??
          "",
        guidUser:
          defaultValues.author?.guidUser ??
          prev.guidUser ??
          currentUser?.guid ??
          "",
        salesChanel: defaultValues.salesChanel ?? prev.salesChanel ?? 1,
        varietyType: defaultValues.varietyType ?? prev.varietyType ?? 2,
        startup3DType: Number(
          defaultValues.startup3DType ?? prev.startup3DType ?? 2
        ),
        type: Number(defaultValues.type ?? prev.type ?? 4),
        externalUrl: defaultValues.externalUrl ?? prev.externalUrl ?? "",
        companyPhoneNumber:
          defaultValues.companyPhoneNumber ??
          prev.companyPhoneNumber ??
          "4992167212",
        feedbackFormUrl:
          defaultValues.feedbackFormUrl ??
          prev.feedbackFormUrl ??
          "https://interior-techno.ru/#order",
        activeFrom: defaultValues.activeFrom ?? prev.activeFrom,
        activeTo: defaultValues.activeTo ?? prev.activeTo,
        guidDestinationGroup:
          defaultValues.guidDestinationGroup ??
          prev.guidDestinationGroup ??
          findGroupByName([t("publicLinkForm.defaults.visits")]),
        guidDestinationGroupForEmployee:
          defaultValues.guidDestinationGroupForEmployee ??
          prev.guidDestinationGroupForEmployee ??
          findGroupByName([t("publicLinkForm.defaults.shows")]),
        idStartCatalogFolder:
          defaultValues.idStartCatalogFolder ??
          prev.idStartCatalogFolder ??
          findGroupByName([
            t("publicLinkForm.defaults.firstContactsShort"),
            t("publicLinkForm.defaults.firstContacts"),
          ]),
        publicLinkUrl: publicLink ?? prev.publicLinkUrl ?? "",
      }));
    }
  }, [defaultValues, groups, currentUser, guidCalculation, t]);

  const [userDisplayFlag, setUserDisplayFlag] = useState(false);
  const userToDisplay = useMemo(() => {
    return userDisplayFlag ? formData.guidUser : ((formData.companyUsers && formData.companyUsers[0]) ? formData.companyUsers[0].userGuid : formData.guidUser);
  })

  const handleChange = (field) => (event) => {
    let { value, type } = event.target;
    if (type === "checkbox") {
      value = event.target.checked;
    }

    // Числовые поля парсим
    const numberFields = [
      "salesChanel",
      "type",
      "startup3DType",
      "varietyType",
    ];
    if (numberFields.includes(field)) {
      value = Number(value);
    }

    if (field === "guidUser") {
      setUserDisplayFlag(true);
    }

    console.log("field:", field, "value:", value, "typeof:", typeof value);

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const currentVariantIndex = () => {
    if (!calculationList || !guidCalculation) return null;
    const index = calculationList.findIndex(
      (item) => item.calculationGuid === guidCalculation
    );
    return index !== -1 ? index : null;
  };

  const handleSaveLink = async () => {
    try {
      const dateStart = formatDateWithLocalOffset(formData.activeFrom);
      const dateEnd = formatDateWithLocalOffset(formData.activeTo);

      const body = {
        type: Number(formData.type),
        guidCalculation:
          formData.guidCalculation || defaultValues.guidCalculation || "",
        guidProject: formData.guidProject || defaultValues.guidProject || "",
        dateActiveFrom: dateStart,
        dateActiveTo: dateEnd,
        salesChannel: formData.salesChanel,
        varietyType: formData.varietyType,
        startup3DType: Number(formData.startup3DType),
        companyPhoneNumber: formData.companyPhoneNumber,
        idStartCatalogFolder: formData.idStartCatalogFolder || null,
        companyUsers: [{ userGuid: formData.guidUser, companyId }],
        feedbackFormUrl: formData.feedbackFormUrl,
        showSaveVariantButton: formData.showSaveVariantButton,
        showPrice: formData.showPrice,
        guidDestinationGroup: formData.guidDestinationGroup,
        guidDestinationGroupAfterSaveVariant:
          formData.guidDestinationGroupAfterSaveVariant,
        guidDestinationGroupForEmployee:
          formData.guidDestinationGroupForEmployee,
        externalUrl: formData.externalUrl,
        idCompany: companyId,
      };

      let url = "";
      let method = "";

      if (defaultValues?.publicLinkUrl) {
        method = "PUT";
        url = `${globalConfig.config.common.api}StructureUrls/UpdatePublicLink`;
        const updateBody = { ...body, publicLinkUrl: defaultValues.publicLinkUrl };

        const response = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(updateBody),
        });

        if (!response.ok) {
          const errText = await response.text();
          console.error("Ошибка обновления:", response.status, errText);
          throw new Error("ERR_UPDATE");
        }

        showSnackbar(t("publicLinkForm.snackbar.updated"));
      } else {
        // Создание новой ссылки
        method = "POST";
        url = `${globalConfig.config.common.api}StructureUrls/GetPublicLink`;

        const response = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(body),
        });

        const rawText = await response.text();
        if (!response.ok) {
          console.error("Ошибка создания:", response.status, rawText);
          throw new Error("ERR_CREATE");
        }

        const linkId = rawText.replace(/^"|"$/g, "");
        const link = `${globalConfig.config.common.chat}/web/public/?id=${linkId}`;

        setPublicLink(link);
        setShowQR(true);

        setFormData((prev) => ({ ...prev, publicLinkUrl: link }));
        showSnackbar(t("publicLinkForm.snackbar.created"));
      }

      updateLinks();
      dispatch(closePublicLinkForm());
      resetModel();
    } catch (error) {
      console.error(error);
      showSnackbar(t("publicLinkForm.snackbar.saveError"), "error");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const updateLinks = () => {
    const fetch = async () => {
      const send = await new Network().GetPublicLinks(companyId, 1, 500);
      dispatch(setList(send?.data));
    };
    fetch();
  };

  const changeModelOpen = () => {
    dispatch(setOpen(true)); // Открывает модалку выбора модели
  };

  const handleDelete = () => {
    confirmDialog({
      message: t("publicLinkForm.confirm.deleteMessage"),
      header: t("publicLinkForm.confirm.deleteHeader"),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      rejectLabel: t("no"),
      acceptLabel: t("yes"),
      accept: async () => {
        try {
          await new Network().DeletePublicLink(defaultValues.publicLinkUrl);
          dispatch(closePublicLinkForm());
          updateLinks();
          showSnackbar(t("publicLinkForm.snackbar.deleted"));
        } catch (error) {
          showSnackbar(t("publicLinkForm.snackbar.deleteError"), "error");
        }
      },
      reject: () => {},
    });
  };

  const resetModel = () => {
    setFormData((prev) => ({ ...prev, calculationName: "", guidCalculation: "", iconPath: "" }));
  };

  const copyLink = () => {
    window.navigator.clipboard.writeText(
      `https://2dchat.system123.ru/web/public/?id=${defaultValues.publicLinkUrl}`
    );
    showSnackbar(t("publicLinkForm.snackbar.linkCopied"));
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        dispatch(closePublicLinkForm());
        resetModel(); // Очищаем модель при закрытии
      }}
    >
      <DialogContent className="public-link">
        <ChangeModel
          onSelectModel={(model) => {
            setFormData((prev) => ({
              ...prev,
              calculationName: model.calculationName,
              guidCalculation: model.calculationGuid,
              iconPath: model.iconPath,
              guidProject: model.projectGuid,
            }));
          }}
        />

        <div className="share">
          <div className="form--row form--row--left">
            <div className="links">
              <div className="infoList--title">{ t("publicLinkForm.title") }</div>

              <div className="select-with-label">
                <div className="period-select">
                  <label className="input-title">{ t("publicLinkForm.labels.from") }</label>
                  <TextField
                    type="date"
                    fullWidth
                    value={formData.activeFrom ? formData.activeFrom.slice(0, 10) : ""}
                    onChange={handleChange("activeFrom")}
                  />
                </div>
                <div className="period-select">
                  <label className="input-title">{ t("publicLinkForm.labels.to") }</label>
                  <TextField
                    type="date"
                    fullWidth
                    value={formData.activeTo ? formData.activeTo.slice(0, 10) : ""}
                    onChange={handleChange("activeTo")}
                  />
                </div>
              </div>

              <div style={{ marginTop: "20px" }}>
                <div
                  className="back-img"
                  style={{
                    backgroundImage: `url(${formData.iconPath ? formData.iconPath.replace(/ /g, "%20") : noPhoto})`,
                  }}
                />
                <div>
                  <div className="select-with-label">
                    <b>{ t("publicLinkForm.labels.ipmName") }:</b> {formData.calculationName || '""'}
                  </div>
                  <button type="button" className="change_model" onClick={changeModelOpen}>
                    { t("publicLinkForm.actions.replaceIpmModel") }
                  </button>
                </div>
              </div>

              <div className="blockView">
                <FormControl fullWidth>
                  <label className="input-title">{ t("publicLinkForm.labels.salesChannel") }</label>
                  <Select value={formData.salesChanel} onChange={handleChange("salesChanel")}>
                    <MenuItem value={1}>{channels[1]}</MenuItem>
                    <MenuItem value={2}>{channels[2]}</MenuItem>
                    <MenuItem value={3}>{channels[3]}</MenuItem>
                    <MenuItem value={4}>{channels[4]}</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <label className="input-title">{ t("publicLinkForm.labels.user") }</label>
                  <Select value={userToDisplay} onChange={handleChange("guidUser")}>
                    {usersList.map((user) => (
                      <MenuItem key={user.guid} value={user.guid}>
                        {user.lastName} {user.name} - {user.login}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <label className="input-title">{ t("publicLinkForm.labels.linkType") }</label>
                  <Select value={formData.varietyType} onChange={handleChange("varietyType")}>
                    <MenuItem value={1}>{types[1]}</MenuItem>
                    <MenuItem value={2}>{types[2]}</MenuItem>
                    <MenuItem value={3}>{types[3]}</MenuItem>
                    <MenuItem value={4}>{types[4]}</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <label className="input-title">{ t("publicLinkForm.labels.clientFolder") }</label>
                  <Select
                    value={formData.guidDestinationGroup}
                    onChange={handleChange("guidDestinationGroup")}
                  >
                    {groups.map((group) => (
                      <MenuItem key={group.guid} value={group.guid}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <label className="input-title">{ t("publicLinkForm.labels.employeeFolder") }</label>
                  <Select
                    value={formData.guidDestinationGroupForEmployee}
                    onChange={handleChange("guidDestinationGroupForEmployee")}
                  >
                    {groups.map((group) => (
                      <MenuItem key={group.guid} value={group.guid}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <label className="input-title">{ t("publicLinkForm.labels.widgetRelativePath") }</label>
                <TextField fullWidth value={formData.externalUrl} onChange={handleChange("externalUrl")} />

                <div className="flex">
                  <div className="select-with-label">
                    <label className="input-title">{ t("publicLinkForm.labels.showSaveButton") }</label>
                    <FormControlLabel
                      control={<Checkbox checked={formData.showSaveVariantButton} onChange={handleChange("showSaveVariantButton")} />}
                    />
                  </div>
                  <div className="select-with-label">
                    <label className="input-title">{ t("publicLinkForm.labels.showPrice") }</label>
                    <FormControlLabel
                      control={<Checkbox checked={formData.showPrice} onChange={handleChange("showPrice")} />}
                    />
                  </div>
                </div>

                {formData.showSaveVariantButton && (
                  <FormControl fullWidth>
                    <label className="input-title">{ t("publicLinkForm.labels.orderFolder") }</label>
                    <Select
                      value={formData.guidDestinationGroupAfterSaveVariant}
                      onChange={handleChange("guidDestinationGroupAfterSaveVariant")}
                    >
                      {groups.map((group) => (
                        <MenuItem key={group.guid} value={group.guid}>
                          {group.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {formData.varietyType === 1 && (
                  <>
                    <div style={{ marginTop: "15px" }}>
                      { t("publicLinkForm.text.publicLinkForVariant") } {currentVariantIndex() ?? "—"}
                    </div>

                    <span>{ t("publicLinkForm.labels.linkView") }</span>
                    <FormControlLabel
                      value="4"
                      control={
                        <Radio
                          checked={formData.type === 4}
                          onChange={() => setFormData((prev) => ({ ...prev, type: 4 }))}
                        />
                      }
                      label="3D"
                      labelPlacement="bottom"
                    />
                    <FormControlLabel value="AR" control={<Radio disabled />} label="AR" labelPlacement="bottom" />
                    <FormControlLabel value="VR" control={<Radio disabled />} label="VR" labelPlacement="bottom" />

                    <br /><br />
                    <span>{ t("publicLinkForm.labels.start3dType") }</span>
                    <FormControlLabel
                      value="1"
                      control={
                        <Radio
                          checked={formData.startup3DType === 1}
                          onChange={() => setFormData((prev) => ({ ...prev, startup3DType: 1 }))}
                        />
                      }
                      label="Vroom"
                      labelPlacement="bottom"
                    />
                    <FormControlLabel
                      value="2"
                      control={
                        <Radio
                          checked={formData.startup3DType === 2}
                          onChange={() => setFormData((prev) => ({ ...prev, startup3DType: 2 }))}
                        />
                      }
                      label={ t("publicLinkForm.labels.vroomTab") }
                      labelPlacement="bottom"
                    />

                    <div style={{ marginTop: "16px" }}>
                      <FormControlLabel
                        control={<Checkbox name="stories" color="primary" disabled />}
                        label={ t("publicLinkForm.labels.runStories") }
                      />
                    </div>
                  </>
                )}

                {formData.varietyType === 3 && (
                  <div className="blockView">
                    <TextField
                      fullWidth
                      placeholder={ t("publicLinkForm.placeholders.phone") }
                      value={formData.companyPhoneNumber}
                      onChange={handleChange("companyPhoneNumber")}
                    />
                    <TreeSelect
                      value={formData.idStartCatalogFolder}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, idStartCatalogFolder: e.value }))
                      }
                      className="md:w-20rem w-full"
                      placeholder={ t("publicLinkForm.placeholders.catalog") }
                    />
                  </div>
                )}

                {formData.varietyType === 4 && (
                  <div className="blockView">
                    <TextField
                      fullWidth
                      placeholder={ t("publicLinkForm.placeholders.feedbackUrl") }
                      value={formData.feedbackFormUrl}
                      onChange={handleChange("feedbackFormUrl")}
                    />
                    <TreeSelect
                      value={formData.idStartCatalogFolder}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, idStartCatalogFolder: e.value }))
                      }
                      className="md:w-20rem w-full"
                      placeholder={ t("publicLinkForm.placeholders.catalog") }
                    />
                  </div>
                )}
              </div>

              {[1, 3, 4].includes(formData.varietyType) && !defaultValues.publicLinkUrl && (
                <div className="form--row send-qr">
                  <button type="button" onClick={handleSaveLink}>
                    { t("publicLinkForm.actions.getQr") }
                  </button>
                </div>
              )}

              {formData.varietyType === 2 && !defaultValues.publicLinkUrl && (
                <div className="form--row send-qr">
                  <button type="button" onClick={handleSaveLink}>
                    { t("publicLinkForm.actions.getWidgetCode") }
                  </button>
                </div>
              )}

              {[1, 3, 4].includes(formData.varietyType) && defaultValues.publicLinkUrl && (
                <div className="qr-view qr-view--display">
                  <QRCodeSVG value={formData.publicLinkUrl} renderAs="svg" size={180} />
                  <div style={{ marginTop: "10px" }}>
                    <input id="copyLink" value={formData.publicLinkUrl || ""} disabled style={{ width: "100%" }} />

                    <div className="tooltip">
                      <i
                        onClick={() => {
                          if (navigator.clipboard && navigator.clipboard.writeText) {
                            navigator.clipboard
                              .writeText(formData.publicLinkUrl)
                              .then(() => showSnackbar(t("publicLinkForm.snackbar.linkCopied")))
                              .catch(() => showSnackbar(t("publicLinkForm.snackbar.copyFailed"), "error"));
                          } else {
                            const textarea = document.createElement("textarea");
                            textarea.value = formData.publicLinkUrl;
                            document.body.appendChild(textarea);
                            textarea.focus();
                            textarea.select();
                            try {
                              document.execCommand("copy");
                              showSnackbar(t("publicLinkForm.snackbar.linkCopied"));
                            } catch (err) {
                              showSnackbar(t("publicLinkForm.snackbar.copyFailed"), "error");
                            }
                            document.body.removeChild(textarea);
                          }
                        }}
                      >
                        <span className="tooltiptext">{ t("companyLink.tooltip.copy") }</span>
                      </i>
                    </div>
                  </div>
                </div>
              )}

              {formData.varietyType === 2 && defaultValues.publicLinkUrl && (
                <div className="blockView">
                  <p className="hint-info">
                    { t("publicLinkForm.hints.widgetIntro") }
                  </p>
                  <div style={{ background: "#f4f4f4", padding: "10px", borderRadius: "8px", maxWidth: "100%", boxSizing: "border-box" }}>
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", boxSizing: "border-box" }}>
                      <code>{`<script type="module">
import * as varwidget from "https://widget.system123.ru/v0/varwidget.js";
window.varwidget = varwidget;
</script>`}</code>
                    </pre>
                  </div>

                  <p className="hint-info">
                    { t("publicLinkForm.hints.widgetPlacement") }
                  </p>
                  <pre>
                    <code>
                      {`<button onclick="varwidget.showPublicLink('${defaultValues.publicLinkUrl}')">`}
                      {t("publicLinkForm.widget.openWidget")}
                      {`</button>`}
                    </code>
                  </pre>

                  <p className="hint-info">
                    { t("publicLinkForm.hints.widgetAlt") }
                  </p>
                  <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", boxSizing: "border-box" }}>
                    <code>
                      &lt;a href="javascript:void varwidget.showPublicLink({defaultValues.publicLinkUrl});"&gt;{t("publicLinkForm.widget.openWidget")}&lt;/a&gt;
                    </code>
                  </pre>

                  <p className="hint-info">
                    { t("publicLinkForm.hints.styleButton") }
                  </p>
                  <pre onClick={() => copyLink()} style={{ cursor: "pointer" }}>
                    <code>{`https://2dchat.system123.ru/web/public/?id=${defaultValues.publicLinkUrl}`}</code>
                  </pre>
                  <p className="hint-info">
                    { t("publicLinkForm.hints.useAsPublicLink") }
                  </p>
                  <a
                    href={`https://widget.system123.ru/v0/public-link/${defaultValues.publicLinkUrl}?fine-tune-public-link`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    { t("publicLinkForm.actions.fineTunePublicLink") }
                  </a>
                </div>
              )}

              {defaultValues.publicLinkUrl && (
                <div className="buttons-menu">
                  <div>
                    <button type="button" onClick={() => dispatch(setOpenAnalytics(true))}>
                      { t("publicLinkForm.actions.analytics") }
                    </button>
                    {localStorage.getItem("companyName") && defaultValues?.externalUrl && (
                      <button
                        type="button"
                        onClick={() => {
                          const companyName = localStorage.getItem("companyName");
                          const relativeUrl = defaultValues.externalUrl;
                          const fullUrl = `https://${companyName}.com${relativeUrl}`;
                          window.open(fullUrl, "_blank");
                        }}
                        style={{ marginLeft: "10px" }}
                      >
                        { t("publicLinkForm.actions.clientPage") }
                      </button>
                    )}
                  </div>
                  <button type="submit" onClick={handleSaveLink}>
                    { t("publicLinkForm.actions.save") }
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      dispatch(closePublicLinkForm());
                      resetModel();
                    }}
                  >
                    { t("publicLinkForm.actions.cancel") }
                  </button>
                  <button type="button" onClick={handleDelete}>
                    { t("publicLinkForm.actions.delete") }
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </DialogContent>
    </Dialog>
  );
};

export default PublicLinkNew;
